import React from 'react';
import { useTranslation } from 'react-i18next';

import { closeIcon } from '@justgiving/icons';
import { Button } from '@justgiving/react-tracking';
import Svg from '@justgiving/svg';
import classNames from 'classnames';
import gql from 'graphql-tag';

import { EncourageDonateOwnerFragment } from '../../../types/__generated__';
import { Experiments } from '../../constants/experiments';
import { usePageOwner } from '../../hooks';
import usePageQuery, {
  UsePageQueryResult,
  buildQuery,
} from '../../hooks/usePageQuery';
import DonateButton from '../DonateButton/DonateButton';
import IconButton from '../IconButton/IconButton';
import socialShareImage from './assets/social-share.svg';

import styles from './EncourageDonateModal.scss';

interface EncourageDonateModalProps {
  onClose: () => void;
}

const FRAGMENT = buildQuery(gql`
  fragment EncourageDonateOwner on Page {
    owner {
      id
      name
      avatar
    }
  }
`);

export const EncourageDonateModal = React.forwardRef(
  ({ onClose }: EncourageDonateModalProps, ref?: React.Ref<HTMLDivElement>) => {
    const { isPageOwner } = usePageOwner();
    const result = usePageQuery<EncourageDonateOwnerFragment>(FRAGMENT);
    const { t } = useTranslation('common', { useSuspense: false });

    if (isPageOwner || !UsePageQueryResult.hasData(result) || !result.data) {
      return null;
    }

    const { owner } = result.data;
    const avatarUrl = owner?.avatar;

    return (
      <div
        aria-label={t('page.encourageDonateModal')}
        className={styles.wrapper}
        data-qa="donate-modal"
        ref={ref}
      >
        <IconButton
          className={styles.close}
          onClick={onClose}
          title={t('page.closeEncourageDonateModal')}
        >
          <Svg markup={closeIcon} />
        </IconButton>
        <div className={styles.image}>
          <img
            alt={t('page.encourageDonateModalSocialShareImage')}
            className={classNames(styles.social, styles.avatar)}
            src={avatarUrl ?? socialShareImage}
          />
        </div>
        <div className={styles.description}>
          <h1 className={styles.heading}>
            {t('page.encourageDonateModalTitle')}
          </h1>
          <div className={styles.message}>
            {t('page.encourageDonateModalMessage')}
          </div>
          <DonateButton
            onClick={onClose}
            trackingDataPageSection="EncourageDonateModal"
            experiments={[
              // B2C_041 - Donation Prompts Part 2
              // Remove this MarkSuccess when removing this experiment
              Experiments.b2c_041_fundraising_page_donation_prompts_2,
              Experiments.b2c_036_donate_button_colour,
              Experiments.b2c_038_secure_donate_buttons,
              // B2C_040 - Proposition Bar
              // Remove this experiment when complete
              Experiments.b2c_040_frp_trust_proposition_bar,
            ]}
            successType="EncourageDonateModalButtonClick"
          />
          <p>
            <Button
              className={styles.later}
              onClick={onClose}
              trackingData={{
                action_name: 'click',
                subtype: 'button',
                page_section: 'donate prompt',
                event: 'click',
                event_value: 'close',
              }}
            >
              {t('page.encourageDonateModalLaterDismiss')}
            </Button>
          </p>
        </div>
      </div>
    );
  },
);
